"use client"

import { HttpTypes } from "@medusajs/types"
import { useState, useEffect, useMemo } from "react"
import { 
  calculateDeliveryEstimate,
  formatDeliveryDate,
  getPickupAvailability 
} from "@/lib/util/warehouse-inventory"
import { WarehouseMetadata } from "@/lib/data/inventory"
import { 
  ClockSolid, 
  ArrowUturnLeft, 
  BuildingStorefront,
  ExclamationCircleSolid 
} from "@medusajs/icons"

interface QuantityDeliveryEstimateProps {
  product: HttpTypes.StoreProduct
  requestedQuantity: number
  className?: string
  warehouseStock?: Record<string, number> // Direct warehouse stock prop
  warehouseMetadata?: Record<string, WarehouseMetadata> // Dynamic warehouse metadata
}

export default function QuantityDeliveryEstimate({ 
  product, 
  requestedQuantity,
  className = "",
  warehouseStock: propWarehouseStock,
  warehouseMetadata
}: QuantityDeliveryEstimateProps) {
  // Use direct prop first, then fallback to metadata
  const warehouseStock = useMemo(() => {
    if (propWarehouseStock) {
      return propWarehouseStock
    }
    
    const allWarehouseStockRaw = product.metadata?.all_warehouse_stock
    
    // Handle both string (serialized) and object formats
    if (typeof allWarehouseStockRaw === 'string') {
      try {
        return JSON.parse(allWarehouseStockRaw)
      } catch {
        return undefined
      }
    } else if (allWarehouseStockRaw && typeof allWarehouseStockRaw === 'object') {
      return allWarehouseStockRaw as Record<string, number>
    }
    
    return undefined
  }, [propWarehouseStock, product.metadata?.all_warehouse_stock])



  // Use server-provided metadata directly - no client-side fetching
  const dynamicMetadata = warehouseMetadata
  
  const [deliveryEstimate, setDeliveryEstimate] = useState(() => 
    calculateDeliveryEstimate(product, requestedQuantity, warehouseStock, dynamicMetadata)
  )

  useEffect(() => {
    const newEstimate = calculateDeliveryEstimate(product, requestedQuantity, warehouseStock, dynamicMetadata)
    setDeliveryEstimate(newEstimate)
  }, [product.id, requestedQuantity, warehouseStock, dynamicMetadata])

  const pickupInfo = getPickupAvailability()

  if (requestedQuantity <= 0) {
    return null
  }

  const { krekStock, totalAvailable, estimatedDelivery } = deliveryEstimate

  // When no stock at all, return null - ProductFacts handles out-of-stock messaging
  // (includes restock notification). Avoids duplicate "Trenutno ni na zalogi" blocks.
  if (totalAvailable <= 0) {
    return null
  }



  return (
    <div className={`space-y-2 ${className}`}>
      {/* Quantity-specific delivery estimate */}
      {krekStock && (krekStock.availableQuantity ?? 0) > 0 && (krekStock.availableQuantity ?? 0) >= requestedQuantity ? (
        /* Full quantity available in Krek */
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="flex items-start gap-x-2">
            <BuildingStorefront className="text-emerald-500 h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-emerald-900">
                {requestedQuantity} {requestedQuantity === 1 ? 'kos' : requestedQuantity === 2 ? 'kosa' : requestedQuantity <= 4 ? 'kosi' : 'kosov'} na voljo v trgovini
              </div>
              <div className="text-emerald-700 text-xs mt-1">
                {pickupInfo.message}
              </div>
              <div className="text-emerald-600 text-xs mt-1">
                Dostava: {formatDeliveryDate(new Date(Date.now() + krekStock.deliveryDays * 24 * 60 * 60 * 1000), true)}
              </div>
            </div>
          </div>
        </div>
      ) : krekStock && krekStock.availableQuantity > 0 && krekStock.availableQuantity < requestedQuantity ? (
        /* Partial stock in Krek */
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-x-3">
            <ExclamationCircleSolid className="text-amber-500 h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-3">
              <div className="font-semibold text-amber-900 text-base">Delna dostopnost</div>
              
              {/* Krek warehouse options */}
              <div className="bg-white rounded-lg p-3 border border-amber-100">
                <div className="flex items-center gap-x-2 mb-2">
                  <BuildingStorefront className="text-amber-600 h-4 w-4" />
                  <span className="font-medium text-amber-800 text-sm">Na voljo v trgovini: {krekStock.availableQuantity} {krekStock.availableQuantity === 1 ? 'kos' : krekStock.availableQuantity === 2 ? 'kosa' : krekStock.availableQuantity <= 4 ? 'kosi' : 'kosov'}</span>
                </div>
                <div className="space-y-1">
                  <div className="text-amber-700 text-xs flex items-center gap-x-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    {estimatedDelivery.pickupMessage}
                  </div>
                  <div className="text-amber-700 text-xs flex items-center gap-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Dostava: {formatDeliveryDate(new Date(Date.now() + krekStock.deliveryDays * 24 * 60 * 60 * 1000), true)}
                  </div>
                </div>
              </div>

              {/* Other warehouses */}
              <div className="bg-white rounded-lg p-3 border border-amber-100">
                <div className="flex items-center gap-x-2 mb-2">
                  <ArrowUturnLeft className="text-amber-600 h-4 w-4" />
                  <span className="font-medium text-amber-800 text-sm">Iz drugih skladišč: {(() => {
                    try {
                      if (!estimatedDelivery?.message) return 'ni na voljo';
                      
                      const otherWarehouseMessage = estimatedDelivery.message.split(', ')[1];
                      if (!otherWarehouseMessage) return 'ni na voljo';
                      
                      // Extract the number from the message (e.g., "1 kosov v 10 dneh")
                      const match = otherWarehouseMessage.match(/(\d+)\s+kosov?\s+v\s+(\d+)\s+dneh/);
                      if (match) {
                        const quantity = parseInt(match[1]);
                        const days = parseInt(match[2]);
                        const kosForm = quantity === 1 ? 'kos' : quantity === 2 ? 'kosa' : quantity <= 4 ? 'kosi' : 'kosov';
                        return `${quantity} ${kosForm} v ${days} dneh`;
                      }
                      return otherWarehouseMessage;
                    } catch (error) {
                      console.error('Error processing other warehouse message:', error);
                      return 'ni na voljo';
                    }
                  })()}</span>
                </div>
                <div className="text-amber-700 text-xs">
                  Predvidena dostava: {formatDeliveryDate(estimatedDelivery.date)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : totalAvailable >= requestedQuantity ? (
        /* Available from other warehouses */
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-x-2">
            <ArrowUturnLeft className="text-blue-500 h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-blue-900">Dostava iz skladišča</div>
              <div className="text-blue-700 text-xs mt-1">
                {estimatedDelivery.message}
              </div>
              <div className="text-blue-600 text-xs">
                Predvidena dostava: {formatDeliveryDate(estimatedDelivery.date)}
              </div>
            </div>
          </div>
        </div>
      ) : totalAvailable > 0 ? (
        /* Insufficient stock */
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start gap-x-2">
            <ExclamationCircleSolid className="text-red-500 h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-red-900">Premalo na zalogi</div>
              <div className="text-red-700 text-xs mt-1">
                Samo {totalAvailable} {totalAvailable === 1 ? 'kos' : totalAvailable <= 4 ? 'kosi' : 'kosov'} na voljo
              </div>
              <div className="text-red-600 text-xs">
                Zmanjšajte količino ali kontaktirajte nas
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* No stock */
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start gap-x-2">
            <ClockSolid className="text-red-500 h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-red-900">Trenutno ni na zalogi</div>
              <div className="text-red-700 text-xs mt-1">
                Kontaktirajte nas za dobavne roke
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
